import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { testimonials } from "../../data/content";
import { ArrowLeft, ArrowRight } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const Testimonials = () => {
  const { isAr, t } = useLang();
  const [i, setI] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % testimonials.length), 9000);
    return () => clearInterval(id);
  }, []);

  const cur = testimonials[i];
  const next = () => setI((x) => (x + 1) % testimonials.length);
  const prev = () => setI((x) => (x - 1 + testimonials.length) % testimonials.length);

  return (
    <section
      className="relative bg-[#0F2F29] text-bg-ivory py-[120px] md:py-[200px] overflow-hidden"
      data-testid="testimonials-section"
    >
      <div className="max-w-[1760px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex justify-between items-center text-bg-ivory/65 mb-16">
          <span className="page-meta">— Client Perspectives</span>
          <span className="page-meta">{t("شهادات الشركاء", "Testimonials")}</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease }}
          className="mb-16 max-w-3xl"
        >
          <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-champagne/80 mb-5`}>
            {t("[ نتائج موثّقة ]", "[ PROVEN OUTCOMES ]")}
          </div>
          <h3
            className={`${isAr ? "arabic-title" : "serif-title"} text-bg-ivory`}
            style={{ fontSize: isAr ? "clamp(36px, 4.6vw, 64px)" : "clamp(46px, 5.2vw, 96px)" }}
          >
            {isAr ? "ما يقوله شركاؤنا" : (<>What partners<br /><em className="font-light">say about us</em></>)}
          </h3>
        </motion.div>

        <div className="editorial-rule-dark mb-16" />

        <div className="grid grid-cols-12 gap-10 items-start min-h-[340px]">
          <div className="col-span-12 md:col-span-9">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${i}-${isAr}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.9, ease }}
              >
                <div
                  className={`${isAr ? "arabic-title" : "serif-title italic font-light"} text-bg-ivory mb-10`}
                  style={{
                    fontSize: isAr ? "clamp(22px, 2.6vw, 38px)" : "clamp(28px, 3.2vw, 54px)",
                    lineHeight: isAr ? 1.7 : 1.25,
                    fontWeight: isAr ? 400 : 300,
                  }}
                  dir={isAr ? "rtl" : "ltr"}
                >
                  <span className="text-champagne">{isAr ? "«" : "“"}</span>
                  {isAr ? cur.ar_quote : cur.en_quote}
                  <span className="text-champagne">{isAr ? "»" : "”"}</span>
                </div>
                <div className="editorial-rule-dark mb-6" style={{ width: 80 }} />
                <div className="flex items-baseline gap-6 flex-wrap">
                  <div className={`${isAr ? "arabic-title" : "font-serif-display"} text-bg-ivory`} style={{ fontSize: 22 }}>
                    {cur.name}
                  </div>
                  <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-champagne/85`}>
                    {isAr ? cur.role_ar : cur.role_en}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="col-span-12 md:col-span-3 flex md:flex-col items-start justify-between gap-6 md:gap-8">
            <div className="page-meta text-bg-ivory/65">
              <span className="text-champagne">{String(i + 1).padStart(2, "0")}</span>
              <span className="mx-2">/</span>
              {String(testimonials.length).padStart(2, "0")}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={prev}
                aria-label="prev"
                className="w-12 h-12 rounded-full border border-champagne/40 flex items-center justify-center hover:bg-champagne hover:text-brand-green transition-colors duration-500 cursor-hover"
                data-testid="testimonial-prev"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                onClick={next}
                aria-label="next"
                className="w-12 h-12 rounded-full border border-champagne/40 flex items-center justify-center hover:bg-champagne hover:text-brand-green transition-colors duration-500 cursor-hover"
                data-testid="testimonial-next"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
